import { useEffect, useRef, useState } from "react"

const Dialog = ({ hidden, onClose, title, description, children, image = false }) => {
  const panel = useRef(null)
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  useEffect(() => {
    if (hidden) return
    const previous = document.activeElement
    const node = panel.current
    const focusables = () => [...node.querySelectorAll('input:not([disabled]), button:not([disabled]), [tabindex="0"]')]
    const firstInput = node.querySelector('input:not([disabled])')
    ;(firstInput || focusables()[0] || node).focus()
    const onKey = (e) => {
      e.stopPropagation()
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); closeRef.current(); }
      if (e.key === "Tab") {
        const items = focusables()
        const first = items[0], last = items[items.length - 1]
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    }
    node.addEventListener("keydown", onKey)
    return () => { node.removeEventListener("keydown", onKey); previous?.focus?.(); }
  }, [hidden])
  if (hidden) return null
  return (
    <div className="popup-bkg" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <section ref={panel} tabIndex={-1} role="dialog" aria-modal="true" aria-label={title}
        className={`popup-body${image ? " popup-guide" : ""}`}>
        <div className="dialog-heading"><h2>{title}</h2><button type="button" className="dialog-close" onClick={onClose} aria-label={`關閉${title}`}>關閉</button></div>
        <p className="dialog-description">{description}</p>
        {children}
      </section>
    </div>
  )
}

export const PopUpInput = ({hidden, setHidden, code, setCode, socket, onMatched}) => {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState("")
  const request = useRef(0)
  const inFlight = useRef(false)
  const endpoint = socket.io?.uri
  useEffect(() => {
    request.current += 1
    inFlight.current = false
    setPending(false)
    setError("")
    return () => { request.current += 1 }
  }, [endpoint])
  const closePairing = () => {
    request.current += 1
    inFlight.current = false
    setPending(false)
    setError("")
    setHidden(true)
  }
  const handleOnSubmit = (e) => {
    e.preventDefault()
    if (inFlight.current) return
    if (code.length !== 6) { setError("請輸入記錄軟體顯示的 6 碼配對碼"); return }
    const version = ++request.current
    inFlight.current = true
    setPending(true)
    setError("")
    socket.emit("gamemapping", { Key_Id: code }, (response) => {
      if (version !== request.current) return
      inFlight.current = false
      setPending(false)
      const matched = response?.status === true
      localStorage.setItem("matched", String(matched))
      onMatched?.(matched)
      if (matched) setHidden(true)
      else setError("配對碼錯誤或連線失敗，請確認後再試一次。")
    })
  }
  return (
    <Dialog hidden={hidden} onClose={closePairing} title="連線記錄軟體" description="開啟記錄軟體，將畫面上的 6 碼配對碼填入下方。">
      <form onSubmit={handleOnSubmit} className="pairing-form">
        <label htmlFor="pairing-code">配對碼</label>
        <input id="pairing-code" className="pairing-code" value={code} onChange={(e) => {setCode(e.target.value); setError("")}}
          maxLength={6} autoComplete="off" spellCheck={false} placeholder="輸入 6 碼配對碼" aria-invalid={!!error} aria-describedby={error ? "pairing-error" : undefined} />
        {error && <p id="pairing-error" className="form-error" role="alert">{error}</p>}
        <div className="dialog-actions"><button type="button" onClick={closePairing}>{pending ? "取消配對" : "稍後配對"}</button><button className="primary-action" type="submit" disabled={pending}>{pending ? "配對中…" : "配對"}</button></div>
        <p className="form-note" role="status">{pending ? "等待記錄軟體回覆；若沒有回應，可取消後重新配對。" : "配對成功後，連線狀態會顯示在記錄面板。"}</p>
      </form>
    </Dialog>
  )
}

export const PopUpImage = ({hidden, setHidden, image}) => (
  <Dialog hidden={hidden} onClose={() => setHidden(true)} title="功能說明" description="使用快捷鍵控制播放與標記；按 Escape 或關閉返回工作台。" image>
    <img className="popup-img" src={image} alt="影片記錄快捷鍵與好球帶操作說明" />
  </Dialog>
)
